import { supabase } from './supabaseClient';

/**
 * Re-run an async operation once if it failed because the Supabase auth
 * lock was "stolen" by a concurrent request. This shows up as either a
 * thrown LockAcquireTimeoutError or an error whose message contains
 * "lock" + "was released" / "stole" — most commonly when two writes fire
 * within a millisecond of each other (e.g. saveScheduleItem firing while
 * the dashboard is also reloading the session). On retry we first
 * explicitly refresh the session so we don't race the underlying token
 * refresh again.
 */
async function withAuthLockRetry(label, op) {
  const looksLikeLockRace = (err) => {
    const msg = String(err?.message || err || '').toLowerCase();
    return msg.includes('lock') && (msg.includes('released') || msg.includes('stole') || msg.includes('timeout'));
  };
  try {
    return await op();
  } catch (err) {
    if (!looksLikeLockRace(err)) throw err;
    console.warn(`[${label}] auth-token lock race; retrying once`, err?.message);
    // Give the previous lock holder time to fully release, then settle the
    // session before retrying so we don't immediately race again.
    await new Promise(r => setTimeout(r, 250));
    try { await supabase.auth.getSession(); } catch (_) {}
    return await op();
  }
}

/**
 * Проверить, является ли строка валидным UUID
 */
function isValidUUID(str) {
  if (!str) return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Получить текущего пользователя (преподавателя)
 */
async function getCurrentTeacher() {
  const { data: { user: authUser } } = await supabase.auth.getUser();
  if (!authUser) {
    return null;
  }
  
  const { data: teacher } = await supabase
    .from('teachers')
    .select('id, auth_user_id')
    .eq('auth_user_id', authUser.id)
    .maybeSingle();
  
  if (!teacher) {
    return null;
  }
  
  return {
    ...teacher,
    id: teacher.auth_user_id,
    role: 'teacher'
  };
}

/**
 * Конвертировать studentId (может быть slug или UUID) в UUID
 */
async function resolveStudentId(studentId) {
  if (!studentId) return null;
  
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
  
  if (isUUID) {
    return studentId;
  }
  
  // Если это slug, пробуем найти student.id по invite_slug
  // Сначала пробуем с дефисом в начале (если его нет)
  let slugToSearch = studentId;
  if (!slugToSearch.startsWith('-')) {
    slugToSearch = `-${slugToSearch}`;
  }
  
  let { data: studentData } = await supabase
    .from('students')
    .select('id')
    .eq('invite_slug', slugToSearch)
    .maybeSingle();
  
  // Если не нашли с дефисом, пробуем без дефиса
  if (!studentData && slugToSearch.startsWith('-')) {
    slugToSearch = slugToSearch.replace(/^-+/, '');
    const { data } = await supabase
      .from('students')
      .select('id')
      .eq('invite_slug', slugToSearch)
      .maybeSingle();
    studentData = data;
  }
  
  // Если все еще не нашли, пробуем исходный studentId
  if (!studentData) {
    const { data } = await supabase
      .from('students')
      .select('id')
      .eq('invite_slug', studentId)
      .maybeSingle();
    studentData = data;
  }
  
  if (studentData) {
    console.log('✅ Found student UUID:', studentData.id, 'for slug:', studentId);
    return studentData.id;
  }
  
  console.warn('❌ Student not found by slug:', studentId, 'tried:', slugToSearch);
  return null;
}

/**
 * Конвертировать запись расписания из localStorage формата в Supabase формат
 */
async function convertScheduleToSupabaseFormat(scheduleItem) {
  const studentId = await resolveStudentId(scheduleItem.studentId);
  
  // Обрабатываем дату: если уже в формате YYYY-MM-DD, используем напрямую
  let dateString = null;
  if (scheduleItem.date) {
    if (typeof scheduleItem.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(scheduleItem.date)) {
      // Уже в формате YYYY-MM-DD
      dateString = scheduleItem.date;
    } else {
      // Парсим дату и конвертируем в YYYY-MM-DD без timezone проблем
      const date = new Date(scheduleItem.date);
      if (!isNaN(date.getTime())) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateString = `${year}-${month}-${day}`;
      }
    }
  }
  
  const result = {
    student_id: studentId,
    group_id: scheduleItem.groupId || null,
    type: scheduleItem.type || 'lesson',
    title: scheduleItem.title || '',
    date: dateString,
    time_from: scheduleItem.from || scheduleItem.timeFrom || null,
    time_to: scheduleItem.to || scheduleItem.timeTo || null,
    all_day: scheduleItem.allDay || false,
    status: scheduleItem.status || 'planned',
    recurring_id: scheduleItem.recurringId || null,
    repeat_weekly: scheduleItem.repeatWeekly || false
  };
  
  // Добавляем id только если это валидный UUID
  if (scheduleItem.id && isValidUUID(scheduleItem.id)) {
    result.id = scheduleItem.id;
  }
  
  return result;
}

/**
 * Конвертировать запись расписания из Supabase формата в localStorage формат
 */
function convertScheduleFromSupabaseFormat(scheduleItem) {
  if (!scheduleItem) return null;
  
  // Обрабатываем дату: если в формате YYYY-MM-DD, конвертируем в Date и обратно в строку для совместимости
  let dateString = scheduleItem.date;
  if (dateString && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Если дата в формате YYYY-MM-DD, создаем Date объект для совместимости
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    dateString = date.toDateString();
  } else if (!dateString) {
    dateString = new Date().toDateString();
  }
  
  return {
    id: scheduleItem.id,
    studentId: scheduleItem.student_id || null,
    groupId: scheduleItem.group_id || null,
    type: scheduleItem.type || 'lesson',
    title: scheduleItem.title || '',
    date: dateString,
    from: scheduleItem.time_from || null,
    to: scheduleItem.time_to || null,
    timeFrom: scheduleItem.time_from || null,
    timeTo: scheduleItem.time_to || null,
    allDay: scheduleItem.all_day || false,
    status: scheduleItem.status || 'planned',
    recurringId: scheduleItem.recurring_id || null,
    repeatWeekly: scheduleItem.repeat_weekly || false
  };
}

/**
 * Получить все записи расписания текущего преподавателя
 */
export async function getSchedule() {
  try {
    const teacher = await getCurrentTeacher();
    if (!teacher) {
      console.warn('Teacher not authenticated, falling back to localStorage');
      return getScheduleFromLocalStorage();
    }
    
    const { data: teacherRecord } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacher.id)
      .maybeSingle();
    
    if (!teacherRecord) {
      console.warn('Teacher record not found');
      return getScheduleFromLocalStorage();
    }
    
    const { data, error } = await supabase
      .from('schedule')
      .select('*')
      .eq('teacher_id', teacherRecord.id)
      .order('date', { ascending: true })
      .order('time_from', { ascending: true });
    
    if (error) {
      console.error('Error fetching schedule from Supabase:', error);
      return getScheduleFromLocalStorage();
    }
    
    return data.map(convertScheduleFromSupabaseFormat);
  } catch (error) {
    console.error('Error in getSchedule:', error);
    return getScheduleFromLocalStorage();
  }
}

/**
 * Получить расписание за диапазон дат (startDate, endDate — строки YYYY-MM-DD).
 * Меньше данных и быстрее, чем getSchedule().
 */
export async function getScheduleByDateRange(startDate, endDate) {
  try {
    const teacher = await getCurrentTeacher();
    if (!teacher) {
      return getScheduleFromLocalStorage();
    }
    
    const { data: teacherRecord } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacher.id)
      .maybeSingle();
    
    if (!teacherRecord) {
      return getScheduleFromLocalStorage();
    }
    
    const { data, error } = await supabase
      .from('schedule')
      .select('*')
      .eq('teacher_id', teacherRecord.id)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true })
      .order('time_from', { ascending: true });
    
    if (error) {
      console.error('Error fetching schedule by range from Supabase:', error);
      return [];
    }
    
    return (data || []).map(convertScheduleFromSupabaseFormat);
  } catch (error) {
    console.error('Error in getScheduleByDateRange:', error);
    return [];
  }
}

/**
 * Получить расписание для конкретного студента
 */
export async function getScheduleForStudent(studentId) {
  try {
    console.log('getScheduleForStudent called with studentId:', studentId);
    
    // Конвертируем studentId из slug в UUID, если нужно
    let actualStudentId = studentId;
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(studentId);
    
    if (!isUUID) {
      const { data: studentData } = await supabase
        .from('students')
        .select('id')
        .eq('invite_slug', studentId)
        .maybeSingle();
      
      if (studentData) {
        actualStudentId = studentData.id;
        console.log('Found student ID by slug:', actualStudentId);
      } else {
        console.warn('Student not found by slug:', studentId);
        return [];
      }
    }
    
    // Загружаем расписание для студента
    const { data, error } = await supabase
      .from('schedule')
      .select('*')
      .eq('student_id', actualStudentId)
      .order('date', { ascending: true })
      .order('time_from', { ascending: true });
    
    if (error) {
      console.error('Error fetching schedule for student from Supabase:', error);
      return [];
    }
    
    console.log('getScheduleForStudent - loaded schedule items:', data?.length || 0);
    return data.map(convertScheduleFromSupabaseFormat);
  } catch (error) {
    console.error('Error in getScheduleForStudent:', error);
    return [];
  }
}

/**
 * Сохранить запись в расписании
 */
export async function saveScheduleItem(scheduleItem) {
  return withAuthLockRetry('saveScheduleItem', async () => {
    const teacher = await getCurrentTeacher();
    if (!teacher) {
      throw new Error('Teacher not authenticated');
    }
    
    const { data: teacherRecord } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacher.id)
      .maybeSingle();
    
    if (!teacherRecord) {
      throw new Error('Teacher record not found');
    }
    
    const supabaseItem = await convertScheduleToSupabaseFormat(scheduleItem);
    supabaseItem.teacher_id = teacherRecord.id;
    supabaseItem.updated_at = new Date().toISOString();
    
    // Проверяем, является ли ID валидным UUID
    const hasValidUUID = scheduleItem.id && scheduleItem.id !== 'new' && isValidUUID(scheduleItem.id);
    
    if (hasValidUUID) {
      // Обновляем существующую запись (только если ID - валидный UUID)
      const { data, error } = await supabase
        .from('schedule')
        .update({
          student_id: supabaseItem.student_id,
          group_id: supabaseItem.group_id,
          type: supabaseItem.type,
          title: supabaseItem.title,
          date: supabaseItem.date,
          time_from: supabaseItem.time_from,
          time_to: supabaseItem.time_to,
          all_day: supabaseItem.all_day,
          status: supabaseItem.status,
          recurring_id: supabaseItem.recurring_id,
          repeat_weekly: supabaseItem.repeat_weekly,
          updated_at: supabaseItem.updated_at
        })
        .eq('id', scheduleItem.id)
        .eq('teacher_id', teacherRecord.id)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating schedule item in Supabase:', error);
        throw error;
      }
      
      const result = convertScheduleFromSupabaseFormat(data);
      
      // Диспатчим событие обновления расписания
      console.log('📅 Dispatching scheduleUpdated event (update)');
      window.dispatchEvent(new CustomEvent('scheduleUpdated', {
        detail: { action: 'update', item: result }
      }));
      
      return result;
    } else {
      // Создаем новую запись (не передаем id, Supabase сам сгенерирует UUID)
      delete supabaseItem.id; // Удаляем id, если он не валидный UUID
      
      const { data, error } = await supabase
        .from('schedule')
        .insert(supabaseItem)
        .select()
        .single();
      
      if (error) {
        console.error('Error creating schedule item in Supabase:', error);
        throw error;
      }
      
      const result = convertScheduleFromSupabaseFormat(data);
      
      // Диспатчим событие обновления расписания
      window.dispatchEvent(new CustomEvent('scheduleUpdated', {
        detail: { action: 'create', item: result }
      }));
      
      return result;
    }
  });
}

/**
 * Удалить запись из расписания
 */
export async function deleteScheduleItem(itemId) {
  try {
    // Проверяем, является ли ID валидным UUID
    if (!isValidUUID(itemId)) {
      console.warn('Cannot delete schedule item: ID is not a valid UUID:', itemId);
      // Если ID не UUID, значит это запись из localStorage, просто возвращаем успех
      return true;
    }
    
    const teacher = await getCurrentTeacher();
    if (!teacher) {
      throw new Error('Teacher not authenticated');
    }
    
    const { data: teacherRecord } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacher.id)
      .maybeSingle();
    
    if (!teacherRecord) {
      throw new Error('Teacher record not found');
    }
    
    const { error } = await supabase
      .from('schedule')
      .delete()
      .eq('id', itemId)
      .eq('teacher_id', teacherRecord.id);
    
    if (error) {
      console.error('Error deleting schedule item from Supabase:', error);
      throw error;
    }
    
    // Диспатчим событие обновления расписания
    console.log('📅 Dispatching scheduleUpdated event (delete)');
    window.dispatchEvent(new CustomEvent('scheduleUpdated', {
      detail: { action: 'delete', itemId }
    }));
    
    return true;
  } catch (error) {
    console.error('Error in deleteScheduleItem:', error);
    throw error;
  }
}

/**
 * Status-only sync for cases where we have a lesson id that may also be a
 * schedule row id (lessons table and schedule table share ids for items
 * created from the calendar). Updates the schedule row if one exists,
 * silently no-ops otherwise, and dispatches scheduleUpdated so widgets
 * that aggregate from the schedule (e.g. Home's "money this month") refresh.
 */
export async function updateScheduleStatusById(id, status) {
  try {
    if (!id || !isValidUUID(id)) return false;
    const teacher = await getCurrentTeacher();
    if (!teacher) return false;
    const { data: teacherRecord } = await supabase
      .from('teachers')
      .select('id')
      .eq('auth_user_id', teacher.id)
      .maybeSingle();
    if (!teacherRecord) return false;

    const { data, error } = await supabase
      .from('schedule')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', id)
      .eq('teacher_id', teacherRecord.id)
      .select();

    if (error) {
      console.error('updateScheduleStatusById error:', error);
      return false;
    }
    if (!data || data.length === 0) return false;

    window.dispatchEvent(new CustomEvent('scheduleUpdated', {
      detail: { action: 'update', item: convertScheduleFromSupabaseFormat(data[0]) }
    }));
    return true;
  } catch (e) {
    console.error('Error in updateScheduleStatusById:', e);
    return false;
  }
}

/**
 * Fallback: загрузить расписание из localStorage
 */
function getScheduleFromLocalStorage() {
  try {
    const stored = localStorage.getItem('novas-lessons');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading schedule from localStorage:', error);
    return [];
  }
}
